export const enumToSentenceCase = (str: string, separator = '_') => {
  const wordArray = str.toLowerCase().split(separator);
  const first = wordArray[0];
  if (first !== undefined) {
    wordArray[0] = capitalizeFirstLetter(first);
  }

  return wordArray.join(' ');
};

export const stringToBool = (str: string): boolean => {
  return str === 'true';
};

export const toSnakeCase = (str: string): string => {
  return str
    .replaceAll(/([a-z])([A-Z])/g, '$1_$2')
    .replaceAll(/[\s_]+/g, '_')
    .toLowerCase();
};

export const toKebabCase = (str: string): string => {
  return str
    .replaceAll(/([a-z])([A-Z])/g, '$1-$2')
    .replaceAll(/[\s_]+/g, '-')
    .toLowerCase();
};

export const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replaceAll(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => (chr ? chr.toUpperCase() : ''))
    .replace(/^[A-Z]/, (match) => match.toLowerCase());
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalizeWords = (str: string): string => {
  return str.replaceAll(/\b\w/g, (char) => char.toUpperCase());
};

export const formatDisplayName = (fullName: string): string => {
  if (!fullName) {
    return '';
  }

  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] ?? '';
  const lastPart = parts.length > 1 ? parts.at(-1) : undefined;
  const lastInitial = lastPart ? lastPart.charAt(0).toUpperCase() : '';

  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
};

const numberFormatter = (amount: number, useCommas = false, showCurrency = false): string => {
  if (!amount) return (showCurrency ? 'R ' : '') + '0.00';
  const isNeg = amount < 0;
  let whole = (amount * (isNeg ? -1 : 1) + '').split('.')[0] ?? '';
  let decimal = (amount + '').split('.')[1];
  if (decimal !== undefined && decimal.length < 2) {
    decimal += '0';
  }
  if (decimal !== undefined && decimal.length > 2) {
    decimal = decimal.slice(0, 2);
  }
  const thousandsCount = (whole.length - 1) / 3;
  if (thousandsCount >= 1) {
    const separator = useCommas ? ',' : ' ';
    for (let i = 0; i < Math.floor(thousandsCount); i++) {
      const split = -3 + -4 * i;
      whole = whole.slice(0, whole.length + split) + separator + whole.slice(split);
    }
  }
  return (showCurrency ? 'R ' : '') + (isNeg ? '-' : '') + whole + '.' + (decimal || '00');
};

export const formatCurrency = (
  amount: number | string,
  useCommas = false,
  showCurrency = true,
): string => {
  const num = typeof amount === 'number' ? amount : Number.parseFloat(amount);
  if (isNaN(num)) {
    return (showCurrency ? 'R ' : '') + '0.00';
  }

  // Round to 2 decimal places to fix floating-point precision issues
  const roundedAmount = Number.parseFloat(num.toFixed(2));
  return numberFormatter(roundedAmount, useCommas, showCurrency);
};

/**
 * A movement rather than a total: always signed, so "+R 300.00" reads as a consequence where a bare
 * "R 300.00" reads as an amount. Zero carries no sign.
 */
export const formatSignedCurrency = (amount: number): string => {
  const magnitude = formatCurrency(Math.abs(amount));
  if (Number(amount.toFixed(2)) === 0) return magnitude;
  return `${amount > 0 ? '+' : '−'}${magnitude}`;
};

export const formatNumber = (amount: number, useCommas = false, showZero = false): string => {
  if (amount === 0 && !showZero) {
    return '-';
  }

  return numberFormatter(amount, useCommas, false);
};

/**
 * Cached per precision — building an `Intl.NumberFormat` is the expensive part, and these are
 * called once per cell.
 */
const decimalFormatters = new Map<number, Intl.NumberFormat>();

const decimalFormatter = (maxDecimals: number): Intl.NumberFormat => {
  const existing = decimalFormatters.get(maxDecimals);

  if (existing) return existing;

  // `en-GB` for a dot decimal separator and predictable grouping; the commas it groups with are
  // swapped for the spaces this app uses below.
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });

  decimalFormatters.set(maxDecimals, formatter);

  return formatter;
};

/**
 * A number written the way a person would: `12` rather than `12.00`, and `12.5` / `12.46` when
 * there actually is a fraction, rounded to `maxDecimals`.
 *
 * Distinct from {@link formatNumber}, which always pads to two decimals and *truncates* the rest
 * (`12.456` → `12.45`). Use this where the value is a count someone reads at a glance — a
 * quantity, a balance — and the trailing zeros are noise. Money keeps its two decimals, so
 * `formatCurrency` is still the right call there.
 *
 * @param value Any finite number; anything else renders as `0`.
 * @param maxDecimals Most decimals to show. Fewer are shown when the value doesn't need them.
 *   Clamped to the 0–100 integer range `Intl.NumberFormat` accepts — it throws a `RangeError`
 *   outside it, and a formatter is not worth crashing a table cell over.
 */
export const formatDecimal = (value: number, maxDecimals = 2): string => {
  const precision = Number.isFinite(maxDecimals)
    ? Math.min(100, Math.max(0, Math.trunc(maxDecimals)))
    : 2;

  return decimalFormatter(precision)
    .format(Number.isFinite(value) ? value : 0)
    .replaceAll(',', ' ');
};

export function getDocumentNameFromUrl(url: string): string {
  const pathname = new URL(url).pathname;
  const decodedName = decodeURIComponent(
    pathname.slice(Math.max(0, pathname.lastIndexOf('/') + 1)),
  );

  const atIndex = decodedName.indexOf(' at ');
  const lastDotIndex = decodedName.lastIndexOf('.');

  if (atIndex !== -1 && lastDotIndex > atIndex) {
    const beforeAt = decodedName.slice(0, Math.max(0, atIndex)); // exclude ' at '
    const extension = decodedName.slice(Math.max(0, lastDotIndex));
    return beforeAt + extension;
  }

  return decodedName;
}

export const formatPascalCase = (str: string) => {
  return [...str].map((c, i) => (i > 0 && c === c.toUpperCase() ? ' ' + c : c)).join('');
};

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const vatNoRegex = /^4\d{9}$/;
export const registrationNoRegex = /^\d{4}\/\d{6}\/\d{2}$|^$/;
export const branchCodeRegex = /^\d{6,}$/;
export const bankAccountNumberRegex = /^\d{7,11}$/;
export const msisdnRegex = /^\d{9,15}$/;
export const noNumbersOrSpecialCharacterRegex = /^[a-zA-Z\s]+$/;
// Context: mirrors the BE's accepted range (PLE-1451) — legacy sites carry 3-char codes, new
// ones may use 4, and both stay valid.
export const SITE_SHORT_CODE_MIN_LENGTH = 3;
export const SITE_SHORT_CODE_MAX_LENGTH = 4;
export const siteShortCodeRegex = new RegExp(
  `^[A-Z0-9]{${SITE_SHORT_CODE_MIN_LENGTH},${SITE_SHORT_CODE_MAX_LENGTH}}$`,
);
